import { KeyframeTrack } from 'three';
import { BVH } from 'three/examples/jsm/loaders/BVHLoader.js';

import { RotationOffsets } from '@cartwhl/common/types';

import { isPositionTrackName } from './rootTranslationTrack';
import { BVHLoader, BVHResult } from './three-overrides/BVHLoader';

// Singleton loader instance with credentials enabled
const loader = new BVHLoader();
loader.setWithCredentials(true);

/**
 * Load multiple BVH files with optional rotation offsets.
 * Uses credentials for authenticated requests.
 */
export async function loadBVHFiles(
  paths: string[],
  rotationOffsets?: RotationOffsets,
): Promise<BVH[]> {
  const validPaths = paths.filter((path) => path.trim() !== '');
  if (!validPaths.length) {
    return [];
  }

  if (rotationOffsets) {
    loader.updateRotationOffsets(rotationOffsets);
  }

  return Promise.all(
    validPaths.map(
      (path) =>
        new Promise<BVH>((resolve, reject) => {
          loader.load(
            path,
            (bvhResult) => resolve(bvhResult as BVH),
            undefined,
            (e) => reject(e),
          );
        }),
    ),
  );
}

/**
 * Manipulate root motion on a keyframe track.
 * Controls which axes (x, y, z) have motion enabled.
 */
export function manipulateRootMotion(
  track: KeyframeTrack,
  xMotion = true,
  yMotion = true,
  zMotion = true,
): void {
  if (isPositionTrackName(track.name)) {
    track.values = track.values.map((value, index) => {
      if (index % 3 === 0) {
        return xMotion ? value : 0;
      } else if ((index + 1) % 3 === 0) {
        return yMotion ? value : 0;
      } else {
        return zMotion ? value : 0;
      }
    });
  } else {
    throw new Error(
      `Only *.position tracks are supported for root motion manipulation. Got ${track.name} instead`,
    );
  }
}

/**
 * Merge multiple BVH files into a single BVH with concatenated animation.
 * Useful for stitching timeline items together.
 */
export function mergeBVHFiles(bvhFiles: BVH[]): BVH {
  if (bvhFiles.length === 0) {
    throw new Error('Cannot merge empty array of BVH files');
  }

  if (bvhFiles.length === 1) {
    return bvhFiles[0];
  }

  // Use the skeleton from the first BVH file
  const mergedSkeleton = bvhFiles[0].skeleton.clone();

  // Group tracks by bone name
  const tracksByName = new Map<string, BVH['clip']['tracks']>();

  let cumulativeDuration = 0;

  for (let fileIndex = 0; fileIndex < bvhFiles.length; fileIndex++) {
    const bvhFile = bvhFiles[fileIndex];
    const clipDuration = bvhFile.clip.duration;

    for (const track of bvhFile.clip.tracks) {
      const trackName = track.name;

      if (!tracksByName.has(trackName)) {
        tracksByName.set(trackName, []);
      }

      const clonedTrack = track.clone();
      if (fileIndex > 0) {
        clonedTrack.shift(cumulativeDuration);
      }

      tracksByName.get(trackName)!.push(clonedTrack);
    }

    cumulativeDuration += clipDuration;
  }

  // Merge tracks with the same name
  const mergedTracks: KeyframeTrack[] = [];

  for (const [trackName, tracks] of tracksByName.entries()) {
    let totalTimesLength = 0;
    let totalValuesLength = 0;

    for (const track of tracks) {
      totalTimesLength += track.times.length;
      totalValuesLength += track.values.length;
    }

    const mergedTimes = new Float64Array(totalTimesLength);
    const mergedValues = new Float64Array(totalValuesLength);

    let timesOffset = 0;
    let valuesOffset = 0;

    for (const track of tracks) {
      mergedTimes.set(track.times, timesOffset);
      mergedValues.set(track.values, valuesOffset);

      timesOffset += track.times.length;
      valuesOffset += track.values.length;
    }

    const firstTrack = tracks[0];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const TrackConstructor = firstTrack.constructor as any;
    const mergedTrack = new TrackConstructor(
      trackName,
      Array.from(mergedTimes),
      Array.from(mergedValues),
    );

    mergedTracks.push(mergedTrack);
  }

  const mergedClip = bvhFiles[0].clip.clone();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mergedClip.tracks = mergedTracks as any;
  mergedClip.duration = cumulativeDuration;
  mergedClip.name = 'merged-animation';

  return {
    clip: mergedClip,
    skeleton: mergedSkeleton,
  };
}

export { BVHLoader, type BVHResult };
